import React, { useState, useEffect, useRef } from 'react';
import {
  Laptop,
  Plus,
  RefreshCw,
  Download,
  Archive,
  Trash2,
  Send,
  CheckCircle2,
  Clock,
  AlertCircle,
  FileCode,
  Tag,
  Search,
  UploadCloud,
  FileArchive,
  X,
  Check,
  Radio,
  ArrowRight,
  RotateCcw,
} from 'lucide-react';

import { appVersionsClient, autoUpdateClient, axiosClient } from '@services/axiosClient';
import type { AppVersionResponse } from '@services/api/generated';
import { env } from '@config/env';
import { toast } from '@components/ui';

// ==========================================
// ĐỊNH NGHĨA TRẠNG THÁI PHIÊN BẢN:
// 0 = Nháp (Draft - Chưa công khai)
// 1 = Publish (Công khai cho các client)
// 2 = Gỡ bỏ (Archived / Ngưng cung cấp)
// ==========================================
export const isDraft = (status?: string | number | null): boolean => {
  return status === 0 || status === '0' || status === 'Draft';
};

export const isPublished = (status?: string | number | null): boolean => {
  return status === 1 || status === '1' || status === 'Published';
};

export const isArchived = (status?: string | number | null): boolean => {
  return status === 2 || status === '2' || status === 'Archived' || status === 'Gỡ bỏ';
};

export const getStatusText = (status?: string | number | null): string => {
  if (isPublished(status)) return '1 • Publish';
  if (isArchived(status)) return '2 • Gỡ bỏ';
  return '0 • Nháp';
};

const FALLBACK_VERSIONS: AppVersionResponse[] = [
  {
    versionId: 104,
    appCode: env.APP_CODE,
    version: '1.2.4',
    major: 1,
    minor: 2,
    build: 4,
    revision: 0,
    channel: 'stable',
    releaseNotes: 'Bổ sung cơ chế AutoUpdate manifest qua Cloud CDN, tối ưu hiệu suất quét tài liệu và sửa lỗi xác thực.',
    fileName: 'BCA.PNR.App-v1.2.4-Setup.zip',
    fileSize: 48520192, // ~46.2 MB
    checksum: 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
    downloadUrl: 'https://pnr-client.ngsxnc.vn/downloads/v1.2.4/setup.zip',
    manifestUrl: 'https://pnr-client.ngsxnc.vn/api/autoupdate/104/manifest',
    isMandatory: true,
    minSupportedVersion: '1.0.0',
    status: 1 as any, // 1 = Publish
    hasPackage: true,
    publishedAt: new Date(Date.now() - 3600000 * 24 * 2),
    regDate: new Date(Date.now() - 3600000 * 24 * 3),
  },
  {
    versionId: 103,
    appCode: env.APP_CODE,
    version: '1.2.3',
    major: 1,
    minor: 2,
    build: 3,
    revision: 0,
    channel: 'stable',
    releaseNotes: 'Cập nhật thư viện kết nối thiết bị ngoại vi và nâng cấp giao thức mã hoá TLS 1.3.',
    fileName: 'BCA.PNR.App-v1.2.3-Setup.zip',
    fileSize: 47290112,
    checksum: 'a5893a772b05b8b9f1d07c0827170a48b99c7170135d0e7293b749d682498522',
    downloadUrl: 'https://pnr-client.ngsxnc.vn/downloads/v1.2.3/setup.zip',
    manifestUrl: 'https://pnr-client.ngsxnc.vn/api/autoupdate/103/manifest',
    isMandatory: false,
    status: 1 as any, // 1 = Publish
    hasPackage: true,
    publishedAt: new Date(Date.now() - 3600000 * 24 * 14),
    regDate: new Date(Date.now() - 3600000 * 24 * 15),
  },
  {
    versionId: 105,
    appCode: env.APP_CODE,
    version: '1.3.0-beta',
    major: 1,
    minor: 3,
    build: 0,
    revision: 1,
    channel: 'beta',
    releaseNotes: 'Phiên bản thử nghiệm giao diện Dark Obsidian và tính năng nhận diện hồ sơ tự động.',
    fileName: 'Chưa có file',
    fileSize: 0,
    status: 0 as any, // 0 = Nháp
    hasPackage: false,
    isMandatory: false,
    regDate: new Date(),
  },
  {
    versionId: 102,
    appCode: env.APP_CODE,
    version: '1.2.2',
    major: 1,
    minor: 2,
    build: 2,
    revision: 0,
    channel: 'stable',
    releaseNotes: 'Bản phát hành cũ đã được thu hồi và gỡ bỏ để thay thế bằng phiên bản v1.2.3.',
    fileName: 'BCA.PNR.App-v1.2.2-Setup.zip',
    fileSize: 45190112,
    checksum: 'c2393a772b05b8b9f1d07c0827170a48b99c7170135d0e7293b749d682498999',
    downloadUrl: 'https://pnr-client.ngsxnc.vn/downloads/v1.2.2/setup.zip',
    isMandatory: false,
    status: 2 as any, // 2 = Gỡ bỏ
    hasPackage: true,
    publishedAt: new Date(Date.now() - 3600000 * 24 * 30),
    regDate: new Date(Date.now() - 3600000 * 24 * 31),
  },
];

export const AppVersionsPage: React.FC = () => {
  const [versions, setVersions] = useState<AppVersionResponse[]>(FALLBACK_VERSIONS);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [channelFilter, setChannelFilter] = useState<'all' | 'stable' | 'beta'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | '0' | '1' | '2'>('all');

  // Modal Create Version states (Bước 1)
  const [showAddModal, setShowAddModal] = useState(false);
  const [newVersion, setNewVersion] = useState('');
  const [newChannel, setNewChannel] = useState('stable');
  const [newReleaseNotes, setNewReleaseNotes] = useState('');
  const [newIsMandatory, setNewIsMandatory] = useState(false);

  // Modal Upload & Publish Package states (Bước 2 & Bước 3)
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [modalStep, setModalStep] = useState<'upload' | 'publish'>('upload');
  const [selectedVersionForUpload, setSelectedVersionForUpload] = useState<AppVersionResponse | null>(null);
  const [selectedZipFile, setSelectedZipFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Simulator Modal: Client Check Version Test
  const [showCheckModal, setShowCheckModal] = useState(false);
  const [simCurrentVersion, setSimCurrentVersion] = useState('1.2.3');
  const [simChannel, setSimChannel] = useState('stable');
  const [simChecking, setSimChecking] = useState(false);
  const [simResult, setSimResult] = useState<any>(null);

  const fetchVersions = async () => {
    setIsLoading(true);

    try {
      const data = await appVersionsClient.getAll();
      if (Array.isArray(data) && data.length > 0) {
        setVersions(data);

      } else {
        setVersions(FALLBACK_VERSIONS);
      }
    } catch (err: any) {
      console.warn('Cannot fetch live versions (likely requires login/token):', err);
      setVersions(FALLBACK_VERSIONS);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchVersions();
  }, []);

  // 1 = PUBLISH: Xuất bản phiên bản -> Chuyển trạng thái sang 1 (Công khai) để client check version thấy được
  const handlePublish = async (versionId: number, versionStr?: string) => {
    setIsPublishing(true);
    try {
      await appVersionsClient.publish(versionId);
      setVersions((prev) =>
        prev.map((v) => (v.versionId === versionId ? { ...v, status: 1 as any, publishedAt: new Date() } : v))
      );
      toast.success(
        `Đã xuất bản phiên bản ${versionStr ? `v${versionStr}` : `#${versionId}`} thành công! Trạng thái chuyển sang: 1 • Publish.`,
        { title: 'Xuất bản công khai (Status: 1)', duration: 4500 }
      );
    } catch (error: any) {
      console.error('Publish failed:', error);
      toast.error(`Không thể xuất bản phiên bản ${versionStr ? `v${versionStr}` : `#${versionId}`}. ${error.message || 'Lỗi hệ thống'}`);
    } finally {
      setIsPublishing(false);
    }
  };

  // 2 = GỠ BỎ: Gọi đến API archive để đổi trạng thái version thành 2 (Gỡ bỏ)
  const handleArchive = async (versionId: number, versionStr?: string) => {
    if (
      !window.confirm(
        `Bạn có chắc muốn gỡ bỏ phiên bản #${versionId} ${versionStr ? `(v${versionStr})` : ''}?\nPhiên bản sẽ chuyển sang trạng thái 2 (Gỡ bỏ) và ngưng phân phối cho các client.`
      )
    ) {
      return;
    }

    try {
      await appVersionsClient.archive(versionId);
      setVersions((prev) =>
        prev.map((v) => (v.versionId === versionId ? { ...v, status: 2 as any } : v))
      );
      toast.success(
        `Đã chuyển phiên bản #${versionId} ${versionStr ? `(v${versionStr})` : ''} sang trạng thái: 2 • Gỡ bỏ!`,
        { title: 'Gỡ bỏ phiên bản (Status: 2)', duration: 4000 }
      );
    } catch (error: any) {
      console.error('Archive failed:', error);
      toast.error(`Không thể gỡ bỏ phiên bản #${versionId}. ${error.message || 'Lỗi hệ thống'}`);
    }
  };

  // Xoá phiên bản vĩnh viễn
  const handleDelete = async (versionId: number, versionStr?: string) => {
    if (!window.confirm(`Bạn có chắc muốn xoá vĩnh viễn phiên bản #${versionId} ${versionStr ? `(v${versionStr})` : ''}?`)) return;
    try {
      await appVersionsClient.delete(versionId);
      setVersions((prev) => prev.filter((v) => v.versionId !== versionId));
      toast.success(`Đã xoá phiên bản #${versionId} thành công.`);
    } catch (error: any) {
      console.error('Delete failed:', error);
      toast.error(`Không thể xoá phiên bản #${versionId}. ${error.message || 'Lỗi hệ thống'}`);
    }
  };

  // Bước 1: Tạo mới version -> Trạng thái mặc định là 0 = Nháp
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newVersion.trim()) return;

    const parts = newVersion.split('.').map((p) => parseInt(p, 10) || 0);
    const body = {
      appCode: env.APP_CODE,
      version: newVersion.trim(),
      major: parts[0] || 1,
      minor: parts[1] || 0,
      build: parts[2] || 0,
      revision: parts[3] || 0,
      channel: newChannel,
      releaseNotes: newReleaseNotes.trim(),
      isMandatory: newIsMandatory,
    };

    let targetVersion: AppVersionResponse;

    try {
      const created = await appVersionsClient.create(body as any);
      targetVersion = { ...created, status: (created.status !== undefined ? created.status : 0) as any };
      setVersions([targetVersion, ...versions]);
      toast.success(`Đã tạo thông tin phiên bản v${targetVersion.version} (Trạng thái: 0 • Nháp). Vui lòng tải lên gói .zip để tiếp tục.`, {
        title: 'Bước 1 hoàn tất',
      });
    } catch (error: any) {
      console.error('Create failed:', error);
      toast.error(`Không thể tạo phiên bản mới. ${error.message || 'Lỗi hệ thống'}`);
      return;
    }

    // Đóng popup tạo mới
    setShowAddModal(false);
    setNewVersion('');
    setNewReleaseNotes('');

    // Chuyển sang Bước 2: Mở hộp thoại upload file .zip
    openUploadModal(targetVersion);
  };

  // Mở modal upload zip (Bước 2)
  const openUploadModal = (version: AppVersionResponse) => {
    setSelectedVersionForUpload(version);
    setSelectedZipFile(null);
    setUploadError(null);
    setModalStep('upload');
    setShowUploadModal(true);
  };

  // Mở modal xuất bản trực tiếp (Bước 3) cho các phiên bản Draft đã có package
  const openPublishModal = (version: AppVersionResponse) => {
    setSelectedVersionForUpload(version);
    setModalStep('publish');
    setShowUploadModal(true);
  };

  // Kiểm tra file đầu vào CHỈ CHO PHÉP .zip
  const validateAndSetZipFile = (file: File) => {
    setUploadError(null);
    const fileName = file.name.toLowerCase();
    const isZip = fileName.endsWith('.zip') || file.type === 'application/zip' || file.type === 'application/x-zip-compressed';

    if (!isZip) {
      setUploadError('Chỉ chấp nhận tệp tin nén định dạng .zip! Vui lòng chọn lại.');
      setSelectedZipFile(null);
      return false;
    }

    setSelectedZipFile(file);
    return true;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      validateAndSetZipFile(e.target.files[0]);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      validateAndSetZipFile(e.dataTransfer.files[0]);
    }
  };

  // Bước 2: Thực hiện upload file .zip lên API
  // Sau khi upload xong, phiên bản vẫn ở trạng thái 0 = Nháp.
  // Tự động chuyển tiếp sang BƯỚC 3: PUBLISH VERSION để chuyển sang trạng thái 1 = Publish!
  const handleUploadZipPackage = async () => {
    if (!selectedVersionForUpload || !selectedZipFile) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedZipFile, selectedZipFile.name);

      // Gọi endpoint /api/admin/app-versions/{versionId}/package
      const response = await axiosClient.post(
        `/api/admin/app-versions/${selectedVersionForUpload.versionId}/package`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 300000, // 5 minutes timeout cho upload file lớn
        }
      );

      const updated = response.data;
      const updatedVersionObj: AppVersionResponse = {
        ...selectedVersionForUpload,
        hasPackage: true,
        fileName: updated?.fileName || selectedZipFile.name,
        fileSize: updated?.fileSize || selectedZipFile.size,
        status: (updated?.status !== undefined ? updated.status : 0) as any, // 0 = Nháp
        downloadUrl: updated?.downloadUrl || selectedVersionForUpload.downloadUrl,
      };

      setVersions((prev) =>
        prev.map((v) => (v.versionId === selectedVersionForUpload.versionId ? updatedVersionObj : v))
      );
      setSelectedVersionForUpload(updatedVersionObj);

      toast.success(`Đã tải lên tệp tin nén .zip (${selectedZipFile.name}) thành công! Chuyển sang bước xuất bản.`, {
        title: 'Bước 2 hoàn tất (Status: 0 • Nháp)',
      });

      // Tự động chuyển sang BƯỚC 3: Xuất bản phiên bản (Publish Version)
      setModalStep('publish');
    } catch (err: any) {
      console.error('Upload package failed:', err);
      toast.error(`Không thể tải lên tệp tin .zip. ${err.message || 'Lỗi hệ thống'}`);
      setUploadError(err.message || 'Lỗi hệ thống khi tải file lên.');
    } finally {
      setIsUploading(false);
    }
  };

  // Bước 3: Xác nhận xuất bản (chuyển trạng thái 0 -> 1) ngay từ modal
  const handleConfirmPublishFromModal = async () => {
    if (!selectedVersionForUpload || !selectedVersionForUpload.versionId) return;
    await handlePublish(selectedVersionForUpload.versionId, selectedVersionForUpload.version);
    setShowUploadModal(false);
  };

  // Đóng modal mà không xuất bản (giữ trạng thái 0 = Nháp)
  const handleSaveAsDraft = () => {
    setShowUploadModal(false);
    if (selectedVersionForUpload) {
      toast.info(
        `Gói cập nhật v${selectedVersionForUpload.version} đã được lưu dưới dạng: 0 • Nháp. Bạn có thể nhấn nút "Xuất bản" trong bảng bất kỳ lúc nào để chuyển sang 1 • Publish.`,
        { title: 'Đã lưu dạng 0 • Nháp', duration: 4500 }
      );
    }
  };

  // Test mô phỏng client gọi check version (/api/autoupdate/check)
  // Chỉ các phiên bản có status === 1 (Publish) mới được trả về cho client!
  const handleRunClientCheckSimulator = async () => {
    setSimChecking(true);
    setSimResult(null);

    try {
      // Thử gọi live autoUpdateClient nếu backend đang bật
      const res = await autoUpdateClient.gET_check(env.APP_CODE, simCurrentVersion, simChannel);
      setSimResult({
        source: 'live',
        data: res,
      });
    } catch {
      // Logic chuẩn của Backend AutoUpdate:
      // CHỈ các phiên bản có trạng thái 1 (Publish) mới được tìm kiếm!
      // Bỏ qua toàn bộ trạng thái 0 (Nháp) và 2 (Gỡ bỏ).
      const publishedVersions = versions.filter(
        (v) => isPublished(v.status) && (simChannel === 'all' || v.channel === simChannel)
      );

      // Lấy phiên bản publish mới nhất
      const latestPublished = publishedVersions[0];

      if (latestPublished && latestPublished.version && latestPublished.version !== simCurrentVersion) {
        setSimResult({
          source: 'simulated',
          hasUpdate: true,
          version: latestPublished.version,
          status: getStatusText(latestPublished.status),
          isMandatory: latestPublished.isMandatory ?? false,
          releaseNotes: latestPublished.releaseNotes,
          downloadUrl: latestPublished.downloadUrl || `https://pnr-client.ngsxnc.vn/downloads/v${latestPublished.version}/setup.zip`,
          manifestUrl: latestPublished.manifestUrl || `https://pnr-client.ngsxnc.vn/api/autoupdate/${latestPublished.versionId}/manifest`,
          publishedAt: latestPublished.publishedAt,
          note: 'Hệ thống AutoUpdate chỉ phân phối phiên bản có trạng thái "1 • Publish". Các bản ghi có trạng thái "0 • Nháp" hoặc "2 • Gỡ bỏ" hoàn toàn không được trả về cho client.',
        });
      } else {
        setSimResult({
          source: 'simulated',
          hasUpdate: false,
          note: `Ứng dụng Client hiện tại (v${simCurrentVersion}) đã là phiên bản mới nhất trên kênh ${simChannel.toUpperCase()}. Không có phiên bản nào ở trạng thái "1 • Publish" cao hơn.`,
        });
      }
    } finally {
      setSimChecking(false);
    }
  };

  // Đếm số lượng theo từng trạng thái: 0, 1, 2
  const countAll = versions.length;
  const countDraft = versions.filter((v) => isDraft(v.status)).length;
  const countPublished = versions.filter((v) => isPublished(v.status)).length;
  const countArchived = versions.filter((v) => isArchived(v.status)).length;

  const filteredVersions = versions.filter((v) => {
    const matchesSearch =
      (v.version || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (v.releaseNotes || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (v.appCode || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesChannel = channelFilter === 'all' || v.channel === channelFilter;

    let matchesStatus = true;
    if (statusFilter === '0') matchesStatus = isDraft(v.status);
    else if (statusFilter === '1') matchesStatus = isPublished(v.status);
    else if (statusFilter === '2') matchesStatus = isArchived(v.status);

    return matchesSearch && matchesChannel && matchesStatus;
  });

  const formatFileSize = (bytes?: number | null) => {
    if (!bytes) return 'Chưa có file';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6 pb-12">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-slate-800 pb-5">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-blue-500/20 bg-blue-500/10 px-3 py-1 text-xs font-semibold text-blue-400">
            <Laptop className="h-3.5 w-3.5" />
            <span>PNR AutoUpdate Ecosystem</span>
          </div>
          <h1 className="mt-2 text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
            Quản lý Phiên bản App Desktop
          </h1>
          <p className="mt-1 text-xs sm:text-sm text-slate-400">
            Quản trị vòng đời phiên bản: <span className="text-amber-300 font-medium">0: Nháp</span> ➔ <span className="text-emerald-300 font-medium">1: Publish</span> ➔ <span className="text-slate-300 font-medium">2: Gỡ bỏ</span>.
          </p>
        </div>

        <div className="flex items-center gap-2.5 shrink-0">
          {/* Nút mở bộ kiểm tra Check Version phía Client */}
          <button
            type="button"
            onClick={() => {
              setSimResult(null);
              setShowCheckModal(true);
            }}
            className="inline-flex items-center gap-1.5 rounded-xl border border-indigo-500/40 bg-indigo-950/40 px-3.5 py-2 text-xs font-semibold text-indigo-300 hover:bg-indigo-900/60 hover:text-white transition-all cursor-pointer shadow-sm"
            title="Kiểm tra kết quả API check version mà các client nhận được"
          >
            <Radio className="h-3.5 w-3.5 text-indigo-400" />
            <span>Test Client Check</span>
          </button>

          <button
            type="button"
            onClick={fetchVersions}
            disabled={isLoading}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-800/60 px-3.5 py-2 text-xs font-semibold text-slate-200 hover:bg-slate-700/80 transition-all cursor-pointer"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin text-blue-400' : ''}`} />
            <span>Làm mới</span>
          </button>

          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2 text-xs font-semibold text-white shadow-lg shadow-blue-500/20 hover:from-blue-500 hover:to-indigo-500 transition-all cursor-pointer"
          >
            <Plus className="h-3.5 w-3.5" />
            <span>Tạo Phiên Bản Mới</span>
          </button>
        </div>
      </div>
      {/* Controls: Search + Channel Filter + Status Filter */}
      <div className="flex flex-col gap-3.5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative flex-1 max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-3.5 w-3.5 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Tìm theo số phiên bản, ghi chú..."
              className="w-full rounded-xl border border-slate-700 bg-[#131d33] py-2 pr-3 pl-9 text-xs text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
            />
          </div>

          {/* Kênh phát hành */}
          <div className="flex items-center gap-1.5 rounded-xl border border-slate-700 bg-[#131d33] p-1 text-xs shrink-0">
            <button
              onClick={() => setChannelFilter('all')}
              className={`rounded-lg px-3 py-1.5 font-medium transition-colors cursor-pointer ${channelFilter === 'all' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'
                }`}
            >
              Tất cả kênh
            </button>
            <button
              onClick={() => setChannelFilter('stable')}
              className={`rounded-lg px-3 py-1.5 font-medium transition-colors cursor-pointer ${channelFilter === 'stable' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'
                }`}
            >
              Kênh Stable
            </button>
            <button
              onClick={() => setChannelFilter('beta')}
              className={`rounded-lg px-3 py-1.5 font-medium transition-colors cursor-pointer ${channelFilter === 'beta' ? 'bg-amber-600 text-white' : 'text-slate-400 hover:text-white'
                }`}
            >
              Kênh Beta
            </button>
          </div>
        </div>

        {/* Lọc theo 3 trạng thái: 0, 1, 2 */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 text-xs">
          <span className="text-slate-400 text-xs font-semibold mr-1">Lọc trạng thái:</span>

          <button
            type="button"
            onClick={() => setStatusFilter('all')}
            className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 font-semibold transition-all cursor-pointer border ${statusFilter === 'all'
              ? 'bg-blue-600/20 border-blue-500 text-blue-300 shadow-sm'
              : 'border-slate-800 bg-[#131d33] text-slate-400 hover:text-white hover:border-slate-700'
              }`}
          >
            <span>Tất cả</span>
            <span className="rounded-full bg-slate-800 px-1.5 py-0.2 text-[10px] text-slate-300 font-mono">
              {countAll}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter('0')}
            className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 font-semibold transition-all cursor-pointer border ${statusFilter === '0'
              ? 'bg-amber-500/20 border-amber-500 text-amber-300 shadow-sm'
              : 'border-slate-800 bg-[#131d33] text-slate-400 hover:text-amber-300 hover:border-amber-500/40'
              }`}
          >
            <Clock className="h-3 w-3 text-amber-400" />
            <span>0 • Nháp</span>
            <span className="rounded-full bg-amber-500/20 px-1.5 py-0.2 text-[10px] text-amber-300 font-mono">
              {countDraft}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter('1')}
            className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 font-semibold transition-all cursor-pointer border ${statusFilter === '1'
              ? 'bg-emerald-500/20 border-emerald-500 text-emerald-300 shadow-sm'
              : 'border-slate-800 bg-[#131d33] text-slate-400 hover:text-emerald-300 hover:border-emerald-500/40'
              }`}
          >
            <CheckCircle2 className="h-3 w-3 text-emerald-400" />
            <span>1 • Publish</span>
            <span className="rounded-full bg-emerald-500/20 px-1.5 py-0.2 text-[10px] text-emerald-300 font-mono">
              {countPublished}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter('2')}
            className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 font-semibold transition-all cursor-pointer border ${statusFilter === '2'
              ? 'bg-zinc-700/50 border-zinc-500 text-slate-200 shadow-sm'
              : 'border-slate-800 bg-[#131d33] text-slate-400 hover:text-white hover:border-slate-700'
              }`}
          >
            <Archive className="h-3 w-3 text-slate-400" />
            <span>2 • Gỡ bỏ</span>
            <span className="rounded-full bg-zinc-800 px-1.5 py-0.2 text-[10px] text-slate-300 font-mono">
              {countArchived}
            </span>
          </button>
        </div>
      </div>

      {/* Versions Table */}
      <div className="overflow-hidden rounded-2xl border border-slate-800 bg-[#0f172a]/80 shadow-xl backdrop-blur-md">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-slate-800 text-slate-400 uppercase tracking-wider text-[11px] bg-slate-900/60">
                <th className="py-3 px-4">Phiên bản & App</th>
                <th className="py-3 px-4">Kênh (Channel)</th>
                <th className="py-3 px-4">Gói cài đặt (.zip)</th>
                <th className="py-3 px-4">Bắt buộc</th>
                <th className="py-3 px-4">Trạng thái (0 / 1 / 2)</th>
                <th className="py-3 px-4">Ngày phát hành</th>
                <th className="py-3 px-4 text-right">Thao tác</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {filteredVersions.map((v) => (
                <tr
                  key={v.versionId}
                  onDoubleClick={() => {
                    if (!v.hasPackage) {
                      openUploadModal(v);
                    } else if (isDraft(v.status)) {
                      openPublishModal(v);
                    }
                  }}
                  className={`transition-colors select-none ${!v.hasPackage
                    ? 'hover:bg-blue-950/25 cursor-pointer'
                    : isDraft(v.status)
                      ? 'hover:bg-amber-950/20'
                      : isPublished(v.status)
                        ? 'hover:bg-emerald-950/20'
                        : 'hover:bg-slate-800/40'
                    }`}
                  title={
                    !v.hasPackage
                      ? 'Kích đúp để tải lên file .zip'
                      : isDraft(v.status)
                        ? 'Kích đúp để mở hộp thoại Xuất bản (Publish sang trạng thái 1)'
                        : undefined
                  }
                >
                  {/* Version & App Code */}
                  <td className="py-3.5 px-4">
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-500/10 text-blue-400 border border-blue-500/20">
                        <Tag className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="font-bold text-white text-sm">v{v.version}</div>
                        <div className="text-[11px] text-slate-400">{v.appCode || env.APP_CODE}</div>
                      </div>
                    </div>
                  </td>

                  {/* Channel */}
                  <td className="py-3.5 px-4">
                    <span
                      className={`inline-block rounded-lg px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider border ${v.channel === 'stable'
                        ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                        : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                        }`}
                    >
                      {v.channel || 'stable'}
                    </span>
                  </td>

                  {/* Package info */}
                  <td className="py-3.5 px-4">
                    {v.hasPackage ? (
                      <div>
                        <div className="text-slate-200 font-medium flex items-center gap-1.5">
                          <FileArchive className="h-3.5 w-3.5 text-blue-400 shrink-0" />
                          <span className="truncate max-w-[200px]" title={v.fileName}>{v.fileName}</span>
                        </div>
                        <div className="text-[11px] text-slate-400 flex items-center gap-1.5 mt-0.5">
                          <FileCode className="h-3 w-3 text-slate-500" />
                          <span>{formatFileSize(v.fileSize)}</span>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openUploadModal(v);
                        }}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-blue-500/50 bg-blue-500/10 px-2.5 py-1 text-[11px] font-medium text-blue-300 hover:bg-blue-500/20 hover:border-blue-400 transition-all cursor-pointer"
                        title="Nhấp để tải lên tệp tin .zip"
                      >
                        <UploadCloud className="h-3.5 w-3.5" />
                        <span>Tải lên file .zip</span>
                      </button>
                    )}
                  </td>

                  {/* Mandatory */}
                  <td className="py-3.5 px-4">
                    {v.isMandatory ? (
                      <span className="rounded bg-red-500/20 border border-red-500/30 px-2 py-0.5 text-[10px] font-semibold text-red-300">
                        Bắt buộc
                      </span>
                    ) : (
                      <span className="text-slate-500 text-[11px]">Tuỳ chọn</span>
                    )}
                  </td>

                  {/* TRẠNG THÁI: 0 (Nháp), 1 (Publish), 2 (Gỡ bỏ) */}
                  <td className="py-3.5 px-4">
                    {isPublished(v.status) && (
                      <span
                        className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 px-2.5 py-0.5 text-[11px] font-bold text-emerald-300 shadow-sm"
                        title="Trạng thái 1: Publish (Công khai cho các máy trạm client)"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                        1 • Publish
                      </span>
                    )}

                    {isDraft(v.status) && (
                      <span
                        className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 border border-amber-500/30 px-2.5 py-0.5 text-[11px] font-bold text-amber-300 shadow-sm"
                        title="Trạng thái 0: Nháp (Chưa công khai cho client)"
                      >
                        <Clock className="h-3.5 w-3.5 text-amber-400" />
                        0 • Nháp
                      </span>
                    )}

                    {isArchived(v.status) && (
                      <span
                        className="inline-flex items-center gap-1.5 rounded-full bg-zinc-800/90 border border-zinc-700 px-2.5 py-0.5 text-[11px] font-bold text-slate-300 shadow-sm"
                        title="Trạng thái 2: Gỡ bỏ (Đã ngưng phát hành cho client)"
                      >
                        <Archive className="h-3.5 w-3.5 text-slate-400" />
                        2 • Gỡ bỏ
                      </span>
                    )}
                  </td>

                  {/* Date */}
                  <td className="py-3.5 px-4 text-slate-400 text-[11px]">
                    {v.publishedAt ? new Date(v.publishedAt).toLocaleDateString('vi-VN') : '—'}
                  </td>

                  {/* Actions */}
                  <td className="py-3.5 px-4 text-right">
                    <div className="inline-flex items-center gap-1.5">
                      {/* Nút Xuất Bản (Chuyển sang 1 • Publish) nếu là trạng thái 0 (Nháp) và đã có package */}
                      {isDraft(v.status) && v.hasPackage && (
                        <button
                          type="button"
                          onClick={() => openPublishModal(v)}
                          title="Xuất bản phiên bản này sang trạng thái 1 • Publish"
                          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600/20 border border-emerald-500/40 px-2.5 py-1 text-xs font-semibold text-emerald-300 hover:bg-emerald-600 hover:text-white transition-all shadow-sm cursor-pointer"
                        >
                          <Send className="h-3.5 w-3.5" />
                          <span>Xuất bản (1)</span>
                        </button>
                      )}

                      {/* Nút Upload Zip nếu chưa có file */}
                      {!v.hasPackage && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openUploadModal(v);
                          }}
                          title="Tải lên file .zip"
                          className="p-1.5 rounded-lg border border-blue-500/40 text-blue-400 hover:bg-blue-500/10 transition-colors cursor-pointer"
                        >
                          <UploadCloud className="h-3.5 w-3.5" />
                        </button>
                      )}

                      {/* Download package */}
                      {v.downloadUrl && (
                        <a
                          href={v.downloadUrl}
                          target="_blank"
                          rel="noreferrer"
                          title="Tải gói cài đặt"
                          className="p-1.5 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                        >
                          <Download className="h-3.5 w-3.5" />
                        </a>
                      )}

                      {/* Khôi phục lại Publish (1) nếu đang ở trạng thái 2 (Gỡ bỏ) */}
                      {isArchived(v.status) && v.hasPackage && (
                        <button
                          type="button"
                          onClick={() => handlePublish(v.versionId!, v.version)}
                          title="Xuất bản lại phiên bản này (Chuyển sang 1 • Publish)"
                          className="p-1.5 rounded-lg border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 hover:border-emerald-400 transition-colors cursor-pointer"
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </button>
                      )}

                      {/* ICON ARCHIVE: Gọi đến api archive để đổi trạng thái version thành 2 (Gỡ bỏ) */}
                      {!isArchived(v.status) && (
                        <button
                          type="button"
                          onClick={() => handleArchive(v.versionId!, v.version)}
                          title="Gỡ bỏ phiên bản (Chuyển sang trạng thái 2 • Gỡ bỏ)"
                          className="p-1.5 rounded-lg border border-amber-500/30 text-amber-400 hover:bg-amber-500/20 hover:border-amber-400 transition-colors cursor-pointer"
                        >
                          <Archive className="h-3.5 w-3.5" />
                        </button>
                      )}

                      {/* Delete version */}
                      <button
                        type="button"
                        onClick={() => handleDelete(v.versionId!, v.version)}
                        title="Xoá phiên bản"
                        className="p-1.5 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-colors cursor-pointer"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal 1: Create Version (Bước 1) */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="w-full max-w-lg rounded-2xl border border-slate-800 bg-[#0f172a] p-6 shadow-2xl">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <div className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600/20 text-blue-400 text-xs font-bold border border-blue-500/30">1</span>
                <h3 className="text-lg font-bold text-white">Tạo phiên bản Desktop App mới</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="text-slate-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-2 text-xs text-slate-400">
              Bước 1: Khai báo số hiệu phiên bản. Phiên bản mới sẽ có trạng thái ban đầu là <strong>0 • Nháp</strong>. Sau khi tạo, bạn tải file .zip và xuất bản sang <strong>1 • Publish</strong>.
            </p>

            <form onSubmit={handleCreate} className="mt-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-300">
                    Số phiên bản (Version) *
                  </label>
                  <input
                    type="text"
                    required
                    value={newVersion}
                    onChange={(e) => setNewVersion(e.target.value)}
                    placeholder="Ví dụ: 1.2.5"
                    className="mt-1 w-full rounded-xl border border-slate-700 bg-[#131d33] px-3 py-2 text-xs text-white focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300">
                    Kênh cập nhật (Channel)
                  </label>
                  <select
                    value={newChannel}
                    onChange={(e) => setNewChannel(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-slate-700 bg-[#131d33] px-3 py-2 text-xs text-white focus:border-blue-500 focus:outline-none"
                  >
                    <option value="stable">Stable (Chính thức)</option>
                    <option value="beta">Beta (Thử nghiệm)</option>
                    <option value="dev">Dev (Phát triển)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300">
                  Ghi chú phát hành (Release Notes)
                </label>
                <textarea
                  rows={3}
                  value={newReleaseNotes}
                  onChange={(e) => setNewReleaseNotes(e.target.value)}
                  placeholder="Mô tả các tính năng mới hoặc bản sửa lỗi..."
                  className="mt-1 w-full rounded-xl border border-slate-700 bg-[#131d33] px-3 py-2 text-xs text-white focus:border-blue-500 focus:outline-none"
                />
              </div>

              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="mandatoryCheck"
                  checked={newIsMandatory}
                  onChange={(e) => setNewIsMandatory(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-700 bg-[#131d33] text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="mandatoryCheck" className="text-xs text-slate-300 cursor-pointer">
                  Cập nhật bắt buộc (Người dùng phải nâng cấp để tiếp tục sử dụng)
                </label>
              </div>

              <div className="mt-6 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="rounded-xl border border-slate-700 px-4 py-2 text-xs font-semibold text-slate-300 hover:bg-slate-800 transition-colors"
                >
                  Huỷ bỏ
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center gap-1.5 rounded-xl bg-blue-600 px-4 py-2 text-xs font-semibold text-white hover:bg-blue-500 transition-colors cursor-pointer"
                >
                  <span>Tiếp tục (Bước 2: Tải lên .zip)</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal 2 & 3: Upload Zip Package & Publish Version Modal */}
      {showUploadModal && selectedVersionForUpload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4">
          <div className="w-full max-w-lg rounded-2xl border border-blue-500/30 bg-[#0f172a] p-6 shadow-2xl relative">
            {/* Modal Step Navigation Bar */}
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <div className="flex items-center gap-2.5">
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${modalStep === 'upload'
                    ? 'bg-blue-600 text-white'
                    : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                    }`}
                >
                  {modalStep === 'upload' ? '2' : <Check className="h-3.5 w-3.5" />}
                </span>
                <span className="text-xs text-slate-400 font-medium">Tải file .zip</span>

                <ArrowRight className="h-3 w-3 text-slate-600" />

                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${modalStep === 'publish'
                    ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
                    : 'bg-slate-800 text-slate-400'
                    }`}
                >
                  3
                </span>
                <span className={`text-xs font-medium ${modalStep === 'publish' ? 'text-emerald-300 font-semibold' : 'text-slate-400'}`}>
                  Xuất bản (Status: 1 • Publish)
                </span>
              </div>

              <button
                type="button"
                onClick={() => setShowUploadModal(false)}
                className="text-slate-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* BƯỚC 2: TẢI LÊN FILE .ZIP */}
            {modalStep === 'upload' && (
              <div className="mt-4 space-y-4">
                <div>
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <UploadCloud className="h-5 w-5 text-blue-400" />
                    <span>Bước 2: Tải lên gói cài đặt ứng dụng (.zip)</span>
                  </h3>
                  <p className="mt-1 text-xs text-slate-400">
                    Phiên bản: <strong className="text-white">v{selectedVersionForUpload.version}</strong> ({selectedVersionForUpload.appCode}) • Trạng thái:{' '}
                    <span className="text-amber-400 font-bold">{getStatusText(selectedVersionForUpload.status)}</span>
                  </p>
                </div>

                {uploadError && (
                  <div className="flex items-center gap-2.5 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-400">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    <span>{uploadError}</span>
                  </div>
                )}

                {/* Drag & Drop Area (CHỈ CHẤP NHẬN .ZIP) */}
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".zip,application/zip,application/x-zip-compressed"
                    onChange={handleFileChange}
                    className="hidden"
                  />

                  <div
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIsDragging(true);
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`flex flex-col items-center justify-center rounded-2xl border-2 border-dashed p-7 text-center transition-all cursor-pointer ${isDragging
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-slate-700 bg-slate-900/50 hover:border-blue-500/60 hover:bg-slate-900/80'
                      }`}
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-600/10 text-blue-400 border border-blue-500/20 shadow-inner">
                      <FileArchive className="h-6 w-6" />
                    </div>
                    <p className="mt-3 text-xs font-semibold text-white">
                      Kéo thả tệp tin <span className="text-blue-400 underline">.zip</span> vào đây, hoặc nhấp để chọn tệp
                    </p>
                    <p className="mt-1 text-[11px] text-slate-400">
                      Yêu cầu bắt buộc: Chỉ chấp nhận định dạng tệp nén <strong>.zip</strong>
                    </p>
                  </div>
                </div>

                {/* Selected File Card */}
                {selectedZipFile && (
                  <div className="flex items-center justify-between rounded-xl border border-slate-700 bg-[#131d33] p-3">
                    <div className="flex items-center gap-3 overflow-hidden">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-500/20 text-blue-400 border border-blue-500/30">
                        <FileArchive className="h-5 w-5" />
                      </div>
                      <div className="truncate text-left">
                        <div className="truncate text-xs font-semibold text-white">{selectedZipFile.name}</div>
                        <div className="text-[10px] text-slate-400 flex items-center gap-2">
                          <span>{(selectedZipFile.size / (1024 * 1024)).toFixed(2)} MB</span>
                          <span className="text-emerald-400 flex items-center gap-0.5">
                            <Check className="h-3 w-3" /> Định dạng .zip hợp lệ
                          </span>
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setSelectedZipFile(null)}
                      className="p-1 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors shrink-0"
                      title="Bỏ chọn tệp này"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}

                {/* Modal Actions */}
                <div className="flex items-center justify-between pt-3 border-t border-slate-800">
                  <button
                    type="button"
                    onClick={() => setShowUploadModal(false)}
                    disabled={isUploading}
                    className="rounded-xl border border-slate-700 px-4 py-2 text-xs font-semibold text-slate-300 hover:bg-slate-800 transition-colors disabled:opacity-50 cursor-pointer"
                  >
                    Huỷ bỏ
                  </button>

                  <button
                    type="button"
                    onClick={handleUploadZipPackage}
                    disabled={!selectedZipFile || isUploading}
                    className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-2 text-xs font-semibold text-white shadow-lg shadow-blue-500/20 hover:from-blue-500 hover:to-indigo-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {isUploading ? (
                      <>
                        <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        <span>Đang tải lên...</span>
                      </>
                    ) : (
                      <>
                        <UploadCloud className="h-4 w-4" />
                        <span>Tải Lên & Chuyển Sang Xuất Bản</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* BƯỚC 3: XUẤT BẢN PHIÊN BẢN (PUBLISH: 0 -> 1) */}
            {modalStep === 'publish' && (
              <div className="mt-4 space-y-4">
                <div>
                  <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 px-3 py-1 text-xs font-semibold text-emerald-400 mb-2">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    <span>Gói .zip đã sẵn sàng</span>
                  </div>
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <Send className="h-5 w-5 text-emerald-400" />
                    <span>Bước 3: Xuất bản phiên bản (Chuyển trạng thái sang 1: Publish)</span>
                  </h3>
                </div>

                {/* Important Business Rule Explanation Banner */}
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3.5 text-xs text-amber-300 space-y-1.5">
                  <div className="flex items-center gap-2 font-bold text-amber-200">
                    <AlertCircle className="h-4 w-4 shrink-0 text-amber-400" />
                    <span>Quy tắc chuyển đổi trạng thái: 0: Nháp ➔ 1: Publish</span>
                  </div>
                  <p className="text-slate-300 leading-relaxed text-[11px]">
                    Sau khi tải lên file .zip, phiên bản hiện ở trạng thái <strong>0 • Nháp (Draft - Chưa công khai)</strong>. Bạn cần thực hiện bước <strong>Xuất bản (Publish)</strong> để chuyển trạng thái sang <strong>1 • Publish (Công khai)</strong>. Khi đó các client khi gọi check version mới nhận được phiên bản mới này.
                  </p>
                </div>

                {/* Package Summary Card */}
                <div className="rounded-xl border border-slate-700/80 bg-[#131d33] p-4 space-y-2.5 text-xs">
                  <div className="flex items-center justify-between pb-2 border-b border-slate-800">
                    <span className="text-slate-400">Ứng dụng:</span>
                    <span className="font-semibold text-white">{selectedVersionForUpload.appCode || env.APP_CODE}</span>
                  </div>
                  <div className="flex items-center justify-between pb-2 border-b border-slate-800">
                    <span className="text-slate-400">Số phiên bản:</span>
                    <span className="font-bold text-emerald-400 text-sm">v{selectedVersionForUpload.version}</span>
                  </div>
                  <div className="flex items-center justify-between pb-2 border-b border-slate-800">
                    <span className="text-slate-400">Kênh phát hành:</span>
                    <span className="font-semibold text-white uppercase">{selectedVersionForUpload.channel}</span>
                  </div>
                  <div className="flex items-center justify-between pb-2 border-b border-slate-800">
                    <span className="text-slate-400">Tệp tin cài đặt:</span>
                    <span className="font-medium text-blue-300 truncate max-w-[220px]">
                      {selectedVersionForUpload.fileName || selectedZipFile?.name || 'Tệp .zip'} ({formatFileSize(selectedVersionForUpload.fileSize || selectedZipFile?.size)})
                    </span>
                  </div>
                  <div className="flex items-center justify-between pb-2 border-b border-slate-800">
                    <span className="text-slate-400">Trạng thái hiện tại:</span>
                    <span className="inline-flex items-center gap-1 font-bold text-amber-300">
                      <Clock className="h-3.5 w-3.5" />
                      0 • Nháp (Chưa công khai)
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Trạng thái sau xuất bản:</span>
                    <span className="inline-flex items-center gap-1 font-bold text-emerald-400">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      1 • Publish (Công khai cho client)
                    </span>
                  </div>
                </div>

                {/* Modal Actions */}
                <div className="flex items-center justify-between pt-3 border-t border-slate-800 gap-2">
                  <button
                    type="button"
                    onClick={handleSaveAsDraft}
                    disabled={isPublishing}
                    className="rounded-xl border border-slate-700 px-4 py-2 text-xs font-semibold text-slate-300 hover:bg-slate-800 transition-colors disabled:opacity-50 cursor-pointer"
                    title="Lưu lại dưới dạng 0 • Nháp để xuất bản sau"
                  >
                    Để sau (Lưu dạng 0 • Nháp)
                  </button>

                  <button
                    type="button"
                    onClick={handleConfirmPublishFromModal}
                    disabled={isPublishing}
                    className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-5 py-2 text-xs font-semibold text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-500 hover:to-teal-500 transition-all disabled:opacity-50 cursor-pointer"
                  >
                    {isPublishing ? (
                      <>
                        <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        <span>Đang xuất bản...</span>
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        <span>Xuất Bản Ngay (Chuyển sang 1)</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal: Client Version Check Simulator (Test API AutoUpdate phía máy trạm) */}
      {showCheckModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4">
          <div className="w-full max-w-lg rounded-2xl border border-indigo-500/30 bg-[#0f172a] p-6 shadow-2xl relative">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-indigo-500/20 text-indigo-400 border border-indigo-500/30">
                  <Radio className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">Kiểm tra Cập nhật như Máy trạm (Client Simulator)</h3>
                  <p className="text-[11px] text-slate-400">Mô phỏng gọi API <code>/api/autoupdate/check</code> từ máy client</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowCheckModal(false)}
                className="text-slate-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-4 space-y-4 text-xs">
              <div className="rounded-lg bg-slate-900/80 p-3 border border-slate-800 text-[11px] text-slate-300 space-y-1">
                <div className="font-semibold text-white flex items-center gap-1.5">
                  <AlertCircle className="h-3.5 w-3.5 text-blue-400" />
                  <span>Nguyên lý phân phối phiên bản:</span>
                </div>
                <p>• Client <strong>chỉ nhận được</strong> các phiên bản có trạng thái <strong>1 • Publish</strong>.</p>
                <p>• Các phiên bản có trạng thái <strong>0 • Nháp</strong> hoặc <strong>2 • Gỡ bỏ</strong> sẽ bị bỏ qua.</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-300">Phiên bản hiện tại của Client</label>
                  <input
                    type="text"
                    value={simCurrentVersion}
                    onChange={(e) => setSimCurrentVersion(e.target.value)}
                    placeholder="Ví dụ: 1.2.3"
                    className="mt-1 w-full rounded-xl border border-slate-700 bg-[#131d33] px-3 py-2 text-white focus:border-indigo-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-300">Kênh cập nhật (Channel)</label>
                  <select
                    value={simChannel}
                    onChange={(e) => setSimChannel(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-slate-700 bg-[#131d33] px-3 py-2 text-white focus:border-indigo-500 focus:outline-none"
                  >
                    <option value="stable">Stable (Chính thức)</option>
                    <option value="beta">Beta (Thử nghiệm)</option>
                  </select>
                </div>
              </div>

              <button
                type="button"
                onClick={handleRunClientCheckSimulator}
                disabled={simChecking}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 font-semibold text-white hover:bg-indigo-500 transition-all disabled:opacity-50 cursor-pointer shadow-lg shadow-indigo-600/20"
              >
                {simChecking ? (
                  <>
                    <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    <span>Đang gọi API kiểm tra...</span>
                  </>
                ) : (
                  <>
                    <Radio className="h-4 w-4" />
                    <span>Gửi Yêu Cầu Kiểm Tra Cập Nhật</span>
                  </>
                )}
              </button>

              {/* Simulation Result Display */}
              {simResult && (
                <div className="rounded-xl border border-slate-700 bg-[#131d33] p-4 space-y-3">
                  <div className="flex items-center justify-between pb-2 border-b border-slate-800">
                    <span className="font-bold text-white">Kết quả trả về cho Client:</span>
                    {simResult.hasUpdate ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/20 border border-emerald-500/30 px-2 py-0.5 text-[10px] font-bold text-emerald-400">
                        <CheckCircle2 className="h-3 w-3" /> CÓ BẢN MỚI (STATUS: 1)
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-700/60 border border-slate-600/50 px-2 py-0.5 text-[10px] font-bold text-slate-300">
                        ĐÃ LÀ MỚI NHẤT
                      </span>
                    )}
                  </div>

                  {simResult.hasUpdate ? (
                    <div className="space-y-2 text-[11px]">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-400">Phiên bản mới tìm thấy:</span>
                        <span className="font-bold text-emerald-400 text-sm">v{simResult.version}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-400">Trạng thái phiên bản:</span>
                        <span className="font-bold text-emerald-400">{simResult.status}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-400">Cập nhật bắt buộc:</span>
                        <span className={simResult.isMandatory ? 'text-red-400 font-semibold' : 'text-slate-300'}>
                          {simResult.isMandatory ? 'Bắt buộc' : 'Tuỳ chọn'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-400">Đường dẫn tải (.zip):</span>
                        <span className="text-blue-400 underline truncate max-w-[200px]" title={simResult.downloadUrl}>
                          {simResult.downloadUrl}
                        </span>
                      </div>
                      <p className="mt-2 text-slate-400 bg-slate-900/60 p-2.5 rounded-lg border border-slate-800">
                        {simResult.note}
                      </p>
                    </div>
                  ) : (
                    <p className="text-slate-300 text-[11px] leading-relaxed bg-slate-900/60 p-2.5 rounded-lg border border-slate-800">
                      {simResult.note}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="mt-5 flex justify-end pt-3 border-t border-slate-800">
              <button
                type="button"
                onClick={() => setShowCheckModal(false)}
                className="rounded-xl border border-slate-700 px-4 py-2 text-xs font-semibold text-slate-300 hover:bg-slate-800 transition-colors cursor-pointer"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
